// PermissionsContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import UserService from '../Services/user.service.js';
import PermissionService from '../Services/permission.service.js';
import Constants from '../assests/Constants/constants.js';
import { useNavigate } from 'react-router-dom';
import AuthService from '../Services/auth.service.js';
export const PermissionsContext = createContext();

const emptyPermissionsState = {
  CannotAddClient: false,
  ViewSubAccountantClients: false,
  Premium: false,
  CantSeeStatusDocuments: false,
  CantSeeCollectionPage: false,
};

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [permissionsState, setPermissionsState] = useState({ ...emptyPermissionsState });
  const [clientPermissionsState, setClientPermissionsState] = useState({
    CannotSeeVat: false,
    ManageOnly: false,
  });
  const [activeCaseId, setActiveCaseId] = useState(null);
  const [subAccountantCaseRestrictions, setSubAccountantCaseRestrictions] = useState([]); // Denied keys for current SubAccountant + case (or global)
  const [loading, setLoading] = useState(true); // To track loading state
  const [user, setUser] = useState(null); // To store user data
  const [userId, setUserId] = useState(null);
  const [userTypeContext, setUserTypeContext] = useState(null); // To store user data
  const [token, setToken] = useState(null); // To store token
  const [isAccountant, setIsAccountant] = useState(false); // To store user type
  const navigate = useNavigate();

  // Function to reset permissions state (logout)
  const resetPermissionsState = useCallback(() => {
    setPermissions([]);
    setPermissionsState({ ...emptyPermissionsState });
    setClientPermissionsState({
      CannotSeeVat: false,
      ManageOnly: false,
    });
    setActiveCaseId(null);
    setSubAccountantCaseRestrictions([]);
    setUser(null);
    setUserId(null);
    setUserTypeContext(null);
    setToken(null);
    setIsAccountant(false);
    setLoading(false);
  }, []);

  // Load (or reload) auth + permissions from localStorage — used on mount and after login
  const refreshPermissions = useCallback(async ({ redirectIfUnauthenticated = true } = {}) => {
    setLoading(true);
    try {
      const currentUser = await UserService.getCurrentUser();
      const currentToken = await UserService.getCurrentToken();
      const userType = await UserService.getCurrentUserType();

      if (currentUser && currentToken) {
        const currentUserId = await AuthService.getCurrentUserTypeId();
        setUserId(currentUserId);
        const userPermissonType = await AuthService.getCurrentUserPermissonType();
        const accountant = userType.toString() === '2';
        setIsAccountant(accountant);

        const permissionsData = await PermissionService.GetPermissions(
          currentUserId,
          userPermissonType,
          currentToken
        );

        const permissionsNames = permissionsData.map(item => item.permission.name);
        setPermissions(permissionsNames || []);

        const updatedPermissionsState = {};
        permissionsNames.forEach(permission => {
          updatedPermissionsState[permission] = true;
        });
        setPermissionsState(updatedPermissionsState);

        setUserTypeContext(userType);
        setUser(currentUser);
        setToken(currentToken);
        setLoading(false);
        return { user: currentUser, token: currentToken, userType };
      }

      resetPermissionsState();
      if (redirectIfUnauthenticated) {
        navigate('/login');
      }
      return null;
    } catch (error) {
      console.error("Error fetching permissions:", error);
      resetPermissionsState();
      if (redirectIfUnauthenticated) {
        navigate('/login');
      }
      return null;
    }
  }, [navigate, resetPermissionsState]);

  useEffect(() => {
    refreshPermissions({ redirectIfUnauthenticated: true });
    // Only on mount — login calls refreshPermissions explicitly after a new session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) return;                 // still logging in…

    if (!activeCaseId) {
      // no client selected → clear
      setClientPermissionsState({
        CannotSeeVat: false,
        ManageOnly: false,
      });
      return;
    }

    const fetchClientPerms = async () => {
      const permRows = await PermissionService.GetPermissions(
        activeCaseId,
        Constants.PermissionUserType.Client,
        token
      );

      /* rows → simple { Premium:true, CannotSeeVat:true, … } */
      const st = {};
      permRows.forEach(r => { st[r.permission.name] = true; });

      setClientPermissionsState(st);
    };

    fetchClientPerms();
  }, [activeCaseId, token]);

  // SubAccountant per-client restrictions: load when SubAccountant and token/activeCaseId change
  useEffect(() => {
    const isSubAccountant = userTypeContext?.toString() === Constants.UserType.SubAccountant;
    if (!token || !isSubAccountant) {
      setSubAccountantCaseRestrictions([]);
      return;
    }
    const fetchRestrictions = async () => {
      const keys = await PermissionService.GetMyRestrictionsForCase(activeCaseId || null, token);
      setSubAccountantCaseRestrictions(keys || []);
    };
    fetchRestrictions();
  }, [token, userTypeContext, activeCaseId]);

  const setActiveCase = (caseId) => setActiveCaseId(caseId);

  return (
    <PermissionsContext.Provider value={{
      permissions,
      permissionsState,
      clientPermissionsState,
      subAccountantCaseRestrictions,
      setActiveCase,
      loading,
      user,
      userId,
      userTypeContext,
      token,
      isAccountant,
      setUser,
      resetPermissionsState,
      refreshPermissions,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};
